import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  User, 
  Mail, 
  Shield, 
  Settings, 
  LogOut,
  Camera,
  Bell,
  CreditCard,
  CheckCircle,
  Save,
  Trash2,
  Lock,
  Phone,
  X,
  Loader2,
  AlertTriangle,
  Coffee
} from 'lucide-react';
import { useTheme } from '../theme/ThemeContext';
import { logout, updateProfile, deleteAccount, updatePasswordWithOtp } from '../redux/slices/authSlice';

const Profile = () => {
  const { theme } = useTheme();
  const dispatch = useDispatch();
  const { user, loading } = useSelector((state) => state.auth);

  const [activeTab, setActiveTab] = useState('account');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
  });
  const [isDirty, setIsDirty] = useState(false);
  
  // Modal States
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Password Update State
  const [newPassword, setNewPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
      });
    }
  }, [user]);

  if (!user) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setIsDirty(true);
  };

  const handleSaveProfile = async () => {
    const resultAction = await dispatch(updateProfile(formData));
    if (updateProfile.fulfilled.match(resultAction)) {
      alert('Profile updated successfully!');
      setIsDirty(false);
    } else {
      alert(resultAction.payload?.message || 'Failed to update profile');
    }
  };

  const handleRequestOtp = () => {
    if (!newPassword || newPassword.length < 6) {
      alert('Please enter a new password (min 6 characters)');
      return;
    }
    // In a real app, this would trigger Firebase SMS
    // For now, we simulate opening the OTP modal
    setShowOtpModal(true);
  };

  const handleVerifyOtp = async () => {
    if (otpCode.length !== 6) {
      alert('Please enter a 6-digit code');
      return;
    }
    
    // Simulating Firebase token
    const idToken = "demo-firebase-token";
    const resultAction = await dispatch(updatePasswordWithOtp({ idToken, newPassword }));
    
    if (updatePasswordWithOtp.fulfilled.match(resultAction)) {
      alert('Password updated successfully!');
      setShowOtpModal(false);
      setNewPassword('');
      setOtpCode('');
    } else {
      alert(resultAction.payload?.message || 'Failed to update password');
    }
  };

  const confirmDeleteAccount = async () => {
    const resultAction = await dispatch(deleteAccount());
    if (deleteAccount.fulfilled.match(resultAction)) {
      setShowDeleteModal(false);
      window.location.href = '/';
    } else {
      alert(resultAction.payload?.message || 'Failed to delete account');
      setShowDeleteModal(false);
    }
  };

  const menuItems = [
    { id: 'account', icon: User, label: 'Account Info' },
    { id: 'security', icon: Shield, label: 'Security' },
    { id: 'notifications', icon: Bell, label: 'Notifications' },
    { id: 'billing', icon: CreditCard, label: 'Billing' },
    { id: 'settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-8 md:space-y-12 animate-in fade-in duration-500 px-4 py-4 md:py-8">
      {/* OTP Modal */}
      {showOtpModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-[32px] p-8 md:p-10 border shadow-2xl animate-in zoom-in duration-300" style={{ backgroundColor: theme.card, borderColor: theme.border }}>
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-xl md:text-2xl font-black uppercase tracking-tight">Verify PIN</h3>
              <button onClick={() => setShowOtpModal(false)}><X size={24} /></button>
            </div>
            <p className="text-xs md:text-sm font-bold opacity-60 mb-8 text-center uppercase tracking-widest leading-relaxed">
              Enter the 6-digit code sent to <br /> <span style={{ color: theme.primary }}>{user.phone}</span>
            </p>
            <input 
              type="text"
              maxLength={6}
              placeholder="000000"
              className="w-full text-center text-3xl md:text-4xl font-black tracking-[10px] md:tracking-[15px] py-6 md:py-8 rounded-2xl border bg-transparent mb-8 outline-none focus:shadow-xl transition-all"
              style={{ borderColor: theme.primary, color: theme.text }}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
            />
            <button 
              onClick={handleVerifyOtp}
              className="w-full py-4 md:py-5 rounded-xl md:rounded-2xl font-black uppercase tracking-widest text-[10px] md:text-xs shadow-lg transition-transform active:scale-95"
              style={{ backgroundColor: theme.primary, color: theme.background }}
            >
              Confirm & Update
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-[32px] p-8 md:p-10 border shadow-2xl animate-in zoom-in duration-300" style={{ backgroundColor: theme.card, borderColor: theme.border }}>
            <div className="flex flex-col items-center text-center space-y-6">
              <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center text-red-500">
                <AlertTriangle size={40} />
              </div>
              <div className="space-y-2">
                <h3 className="text-2xl font-black uppercase tracking-tight text-red-500">Delete Account?</h3>
                <p className="text-xs font-bold opacity-60 uppercase tracking-widest leading-relaxed">
                  This action is permanent. All your data, orders, and information will be lost forever.
                </p>
              </div>
              <div className="flex flex-col w-full gap-3 pt-4">
                <button 
                  onClick={confirmDeleteAccount}
                  className="w-full py-4 rounded-2xl bg-red-500 text-white font-black uppercase tracking-widest text-[10px] shadow-lg hover:bg-red-600 transition-all active:scale-95"
                >
                  Yes, Delete My Account
                </button>
                <button 
                  onClick={() => setShowDeleteModal(false)}
                  className="w-full py-4 rounded-2xl font-black uppercase tracking-widest text-[10px] transition-all hover:bg-black/5"
                  style={{ color: theme.text }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-8 md:gap-12">
        {/* Sidebar */}
        <div className="md:w-1/3 space-y-6 md:space-y-8">
          <div className="p-6 md:p-8 rounded-[32px] md:rounded-[40px] border relative overflow-hidden" style={{ backgroundColor: theme.card, borderColor: theme.border }}>
            <div className="absolute top-0 right-0 w-24 h-24 md:w-32 md:h-32 opacity-5 translate-x-6 md:translate-x-8 translate-y-[-6px] md:translate-y-[-8px]">
              <Coffee size={128} style={{ color: theme.primary }} />
            </div>
            
            <div className="relative space-y-6">
              <div className="relative inline-block">
                <div 
                  className="w-20 h-20 md:w-24 md:h-24 rounded-[20px] md:rounded-3xl flex items-center justify-center font-black text-2xl md:text-3xl uppercase tracking-tighter border-2 border-dashed" 
                  style={{ borderColor: `${theme.primary}40`, color: theme.primary, backgroundColor: `${theme.primary}10` }}
                >
                   {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                </div>
              </div>
              
              <div className="space-y-1">
                <h2 className="text-xl md:text-2xl font-black uppercase tracking-tight" style={{ color: theme.text }}>{user.firstName} {user.lastName}</h2>
                <p className="text-[8px] md:text-[10px] font-bold uppercase opacity-40 tracking-widest">{user.phone}</p>
              </div>

              <div className="pt-4 md:pt-6 space-y-1.5 md:space-y-2 overflow-x-auto no-scrollbar flex md:flex-col gap-2">
                {menuItems.map((item) => (
                  <button 
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`flex items-center space-x-3 p-3 md:p-4 rounded-xl md:rounded-2xl transition-all font-black uppercase tracking-widest text-[8px] md:text-[10px] whitespace-nowrap md:w-full ${activeTab === item.id ? 'shadow-lg' : 'opacity-40 hover:opacity-100 hover:bg-black/5'}`}
                    style={{ 
                      backgroundColor: activeTab === item.id ? theme.primary : 'transparent',
                      color: activeTab === item.id ? theme.background : theme.text
                    }}
                  >
                    <item.icon size={14} md={16} />
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>

              <button 
                onClick={() => dispatch(logout())}
                className="w-full mt-4 md:mt-8 flex items-center space-x-3 p-3 md:p-4 rounded-xl md:rounded-2xl text-red-500 font-black uppercase tracking-widest text-[8px] md:text-[10px] hover:bg-red-500 hover:bg-opacity-10 transition-colors"
              >
                <LogOut size={14} md={16} />
                <span>Logout Session</span>
              </button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="md:w-2/3">
          <div className="p-6 md:p-10 rounded-[32px] md:rounded-[40px] border h-full" style={{ backgroundColor: theme.card, borderColor: theme.border }}>
             {activeTab === 'account' && (
               <div className="space-y-6 md:space-y-8 animate-in slide-in-from-bottom-4">
                 <div className="space-y-1 md:space-y-2">
                   <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tighter" style={{ color: theme.primary }}>Account Info</h2>
                   <p className="text-[10px] md:text-xs font-bold uppercase tracking-widest opacity-40">Manage your personal details</p>
                 </div>

                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-8">
                    <div className="space-y-1 md:space-y-2">
                      <label className="text-[8px] md:text-[10px] font-black uppercase tracking-widest opacity-40 ml-2">First Name</label>
                      <input 
                        name="firstName"
                        className="w-full p-3 md:p-4 rounded-xl md:rounded-2xl border font-bold text-xs md:text-sm bg-transparent outline-none focus:shadow-lg transition-all" 
                        style={{ borderColor: theme.border, color: theme.text }}
                        value={formData.firstName}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="space-y-1 md:space-y-2">
                      <label className="text-[8px] md:text-[10px] font-black uppercase tracking-widest opacity-40 ml-2">Last Name</label>
                      <input 
                        name="lastName"
                        className="w-full p-3 md:p-4 rounded-xl md:rounded-2xl border font-bold text-xs md:text-sm bg-transparent outline-none focus:shadow-lg transition-all" 
                        style={{ borderColor: theme.border, color: theme.text }}
                        value={formData.lastName}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="space-y-1 md:space-y-2 sm:col-span-2">
                      <label className="text-[8px] md:text-[10px] font-black uppercase tracking-widest opacity-40 ml-2">Phone Number</label>
                      <div className="relative">
                        <Phone size={14} className="absolute left-4 top-1/2 -translate-y-1/2 opacity-20" />
                        <input 
                          name="phone"
                          className="w-full pl-12 p-3 md:p-4 rounded-xl md:rounded-2xl border font-bold text-xs md:text-sm bg-transparent outline-none focus:shadow-lg transition-all" 
                          style={{ borderColor: theme.border, color: theme.text }}
                          value={formData.phone}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                 </div>

                 {isDirty && (
                   <div className="pt-6 md:pt-8 border-t" style={{ borderColor: theme.border }}>
                      <button 
                        onClick={handleSaveProfile}
                        disabled={loading}
                        className="px-6 md:px-8 py-3 md:py-4 rounded-xl md:rounded-2xl font-black uppercase tracking-widest text-[8px] md:text-xs shadow-lg transition-all active:scale-95 flex items-center space-x-2"
                        style={{ backgroundColor: theme.primary, color: theme.background }}
                      >
                        {loading ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                        <span>Save Changes</span>
                      </button>
                   </div>
                 )}
               </div>
             )}

             {activeTab === 'security' && (
               <div className="space-y-8 animate-in slide-in-from-bottom-4">
                 <div className="space-y-2">
                   <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tighter" style={{ color: theme.primary }}>Security</h2>
                   <p className="text-[10px] md:text-xs font-bold uppercase tracking-widest opacity-40">Manage your password and protection</p>
                 </div>

                 <div className="space-y-6">
                    <div className="space-y-2">
                      <label className="text-[8px] md:text-[10px] font-black uppercase tracking-widest opacity-40 ml-2">New Password</label>
                      <div className="flex flex-col sm:flex-row gap-4">
                        <input 
                          type="password"
                          className="flex-grow p-3 md:p-4 rounded-xl md:rounded-2xl border font-bold text-xs md:text-sm bg-transparent outline-none" 
                          style={{ borderColor: theme.border, color: theme.text }}
                          placeholder="Min 6 characters"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                        />
                        <button 
                          onClick={handleRequestOtp}
                          className="px-6 py-3 md:py-4 rounded-xl md:rounded-2xl font-black uppercase tracking-widest text-[8px] md:text-[10px] shadow-lg whitespace-nowrap"
                          style={{ backgroundColor: theme.primary, color: theme.background }}
                        >
                          Verify & Update
                        </button>
                      </div>
                      <p className="text-[8px] md:text-[10px] font-bold opacity-40 uppercase ml-2">We will send a 6-digit PIN to {user.phone} to confirm.</p>
                    </div>

                    <div className="pt-8 border-t" style={{ borderColor: theme.border }}>
                       <h4 className="text-red-500 font-black uppercase tracking-widest text-[10px] mb-4">Danger Zone</h4>
                       <button 
                         onClick={() => setShowDeleteModal(true)}
                         className="flex items-center space-x-2 text-red-500 font-black uppercase tracking-widest text-[8px] md:text-[10px] p-4 rounded-2xl border-2 border-red-500 border-opacity-20 hover:bg-red-500 hover:bg-opacity-5 transition-all"
                       >
                         <Trash2 size={16} />
                         <span>Delete Account Permanently</span>
                       </button>
                    </div>
                 </div>
               </div>
             )}

             {activeTab !== 'account' && activeTab !== 'security' && (
               <div className="flex flex-col items-center justify-center h-full min-h-[200px] space-y-4 opacity-20">
                 <Settings size={32} md={48} />
                 <p className="font-black uppercase tracking-widest text-[10px] md:text-sm">Under Construction</p>
               </div>
             )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
